import { config } from '@iheartjane/config';
import type { AppMode, Nullable } from '@iheartjane/types';
import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { useEffect } from 'react';

import { cartSize } from '@jane/shared-ecomm/util';
import { api, createUseQueryHook } from '@jane/shared/data-access';
import type { Cart, EmptyCart } from '@jane/shared/types';

import {
  getLocalCartUuid,
  updateLocalCartUuid,
} from '../localCartUuid/localCartUuid';

export type MaybeCart = Cart | EmptyCart;

/**
 * Cache keys for the v1 cart, namespaced under `['cart', 'v1', …]`.
 *
 * These deliberately do *not* share `['cart', uuid]` with v3's
 * `cartKeys.detail`. `useKioskCart` branches on `c3UseV3Cart` but has to call
 * both hooks on every render, so a shared key means one cache entry backed by
 * two incompatible fetchers, told apart only by `enabled`. React Query v5 has
 * every observer push its options onto the shared query
 * (`QueryObserver.setOptions` -> `Query.setOptions`), so the later-rendered
 * disabled v1 hook overwrote `queryFn` and v3's refetches went to `/v1/carts`
 * — returning a v1-shaped cart into the v3 entry. (v4 only seeded a query's
 * options when the cache first built it, so v3 happened to win.)
 *
 * The `'cart'` prefix is kept so the existing
 * `invalidateQueries({ queryKey: ['cart'] })` call sites still match both
 * versions.
 */
export const v1CartKeys = {
  detail: (uuid: Nullable<string>, paymentMethod?: string): QueryKey =>
    paymentMethod ? ['cart', 'v1', uuid, paymentMethod] : ['cart', 'v1', uuid],
  forUser: (uuid: string): QueryKey => ['cart', 'v1', 'getForUser', uuid],
};

const cartUrl = () => `${config.apiPath}/carts`;

const getCart = async (
  uuid?: Nullable<string>,
  paymentMethod?: string
): Promise<MaybeCart> => {
  if (!uuid) {
    return {};
  }

  // `payment_method` drives whether the backend computes the Jane Pay (ACH)
  // service fee onto the cart. Without it the cart is optimized as non-Jane-Pay
  // and `jane_pay_service_fee_amount`/`order_total` exclude the fee.
  const queryParams = qs.stringify({
    uuid,
    app_mode: 'kiosk',
    payment_method: paymentMethod,
  });
  const { cart } = await api.get<{ cart: MaybeCart }>(
    `${cartUrl()}?${queryParams}`
  );
  return cart;
};

/**
 * Acts like the typical query to get a cart by its uuid, but coordinates
 *  storing the cart uuid when appropriate.
 */
export const useGetCart = (enabled = true) => {
  const cartUuid = getLocalCartUuid();
  const query = useQuery({
    queryFn: () => getCart(cartUuid),
    queryKey: v1CartKeys.detail(cartUuid),
    notifyOnChangeProps: 'all',
    enabled,
  });
  const { data, isSuccess } = query;

  useEffect(() => {
    // If the cart uuid exists, but the cart has 0 products, reset the uuid.
    // Guarded by query.isSuccess, which is false while the query is disabled
    // (e.g. gated off behind the v3 flag), so this no-ops in v3 mode.
    if (cartUuid && isSuccess && cartSize(data) === 0) {
      updateLocalCartUuid(null);
    }
  }, [cartUuid, data, isSuccess]);

  return query;
};

/**
 * Fetches the cart optimized as a Jane Pay (ACH) order so the backend-computed
 * `jane_pay_service_fee_amount` and fee-inclusive `order_total` are populated.
 * Used to show the ACH fee on the payment selection screen so the total on the
 * kiosk matches what the shopper is charged.
 */
export const useGetJanePayCart = createUseQueryHook<
  Nullable<string>,
  MaybeCart
>(
  (uuid) => getCart(uuid, 'jane_pay'),
  (uuid) => v1CartKeys.detail(uuid, 'jane_pay'),
  (uuid) => !!uuid,
  { notifyOnChangeProps: 'all' }
);

interface GetCartForUserParams {
  app_mode: AppMode;
  store_id: number | undefined;
  uuid: string;
}

const getCartForUser = async ({
  app_mode,
  store_id,
  uuid,
}: GetCartForUserParams) => {
  const queryParams = qs.stringify({ uuid, app_mode, store_id });
  const { cart } = await api.get<{ cart: MaybeCart }>(
    `${cartUrl()}/get_for_user?${queryParams}`
  );
  return cart;
};

export const useGetCartForUser = createUseQueryHook<
  GetCartForUserParams,
  MaybeCart
>(getCartForUser, (params: GetCartForUserParams) =>
  v1CartKeys.forUser(params.uuid)
);
